// src/services/ipLocationService.js

class IPLocationService {
  constructor() {
    this.currentPosition = null;
    this.error = null;
    this.lastFetchTime = null;
    this.cacheDuration = 5 * 60 * 1000; // 5 minutes cache
  }

  /**
   * Get location from IP address (no permission required)
   * Uses multiple free APIs with fallback
   * @returns {Promise<{latitude: number, longitude: number, city: string, region: string, country: string, postal: string, accuracy: string, timestamp: number}>}
   */
  async getLocationFromIP() {
    // Check cache first
    if (this.currentPosition && this.lastFetchTime) {
      const now = Date.now();
      if (now - this.lastFetchTime < this.cacheDuration) {
        return this.currentPosition;
      }
    }

    // Try multiple services with fallback
    const services = [
      this.fetchFromIpInfo,     // ipinfo.io - usually works
      this.fetchFromIPWhoIs,    // ipwhois.io - good fallback
      this.fetchFromIpData,     // ipdata.co - another option
      this.fetchFromGeoplugin   // geoplugin.net - JSONP approach
    ];

    let lastError = null;

    for (const service of services) {
      try {
        console.log(`Attempting to fetch location from: ${service.name}`);
        const result = await service.call(this);
        if (result && result.latitude && result.longitude && result.latitude !== 0) {
          console.log(`✅ Location fetched from: ${service.name}`);
          this.currentPosition = result;
          this.lastFetchTime = Date.now();
          this.error = null;
          return result;
        }
      } catch (error) {
        lastError = error;
        console.warn(`❌ Service ${service.name} failed:`, error.message);
        // Continue to next service
      }
    }

    // If all services fail, use fallback location
    console.error('All IP location services failed:', lastError);
    return this.getFallbackLocation();
  }

  /**
   * Fetch from ipinfo.io (Reliable, supports CORS)
   */
  async fetchFromIpInfo() {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    try {
      const response = await fetch('https://ipinfo.io/json', {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json'
        }
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error.message || 'IP location failed');
      }
      
      // ipinfo returns location as "lat,lon"
      const [latitude, longitude] = data.loc ? data.loc.split(',').map(Number) : [0, 0];
      
      return {
        latitude: latitude || 0,
        longitude: longitude || 0,
        city: data.city || 'Unknown',
        region: data.region || 'Unknown',
        country: data.country || 'Unknown',
        countryCode: data.country || 'Unknown',
        postal: data.postal || '',
        timezone: data.timezone || '',
        accuracy: 'IP-based (approximate)',
        source: 'ipinfo.io',
        timestamp: Date.now()
      };
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw error;
    }
  }

  /**
   * Fetch from ipwhois.io (Supports CORS)
   */
  async fetchFromIPWhoIs() {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    try {
      const response = await fetch('https://ipwhois.io/json/', {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json'
        }
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success === false) {
        throw new Error(data.message || 'IP location failed');
      }
      
      return {
        latitude: parseFloat(data.latitude) || 0,
        longitude: parseFloat(data.longitude) || 0,
        city: data.city || 'Unknown',
        region: data.region || 'Unknown',
        country: data.country || 'Unknown',
        countryCode: data.country_code || 'Unknown',
        postal: data.postal || '',
        timezone: data.timezone || '',
        accuracy: 'IP-based (approximate)',
        source: 'ipwhois.io',
        timestamp: Date.now()
      };
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw error;
    }
  }

  /**
   * Fetch from ipdata.co (Supports CORS)
   */
  async fetchFromIpData() {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    try {
      // Using free tier without API key (limited)
      const response = await fetch('https://api.ipdata.co/', {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json'
        }
      });
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.error) {
        throw new Error(data.error.message || 'IP location failed');
      }
      
      return {
        latitude: data.latitude || 0,
        longitude: data.longitude || 0,
        city: data.city || 'Unknown',
        region: data.region || 'Unknown',
        country: data.country_name || 'Unknown',
        countryCode: data.country_code || 'Unknown',
        postal: data.postal || '',
        timezone: data.time_zone?.name || '',
        accuracy: 'IP-based (approximate)',
        source: 'ipdata.co',
        timestamp: Date.now()
      };
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Request timeout');
      }
      throw error;
    }
  }

  /**
   * Fetch from geoplugin.net (JSONP approach)
   */
  async fetchFromGeoplugin() {
    return new Promise((resolve, reject) => {
      const callbackName = 'geoplugin_callback_' + Date.now();
      
      // Set timeout
      const timeout = setTimeout(() => {
        cleanup();
        reject(new Error('Geoplugin request timeout'));
      }, 5000);
      
      // Cleanup function
      const cleanup = () => {
        clearTimeout(timeout);
        delete window[callbackName];
        if (script.parentNode) {
          script.parentNode.removeChild(script);
        }
      };
      
      // Create script element
      const script = document.createElement('script');
      script.src = `http://www.geoplugin.net/json.gp?jsoncallback=${callbackName}`;
      
      // Define callback
      window[callbackName] = function(data) {
        cleanup();
        
        if (!data || data.geoplugin_status === 404) {
          reject(new Error('IP location failed'));
          return;
        }
        
        const latitude = parseFloat(data.geoplugin_latitude) || 0;
        const longitude = parseFloat(data.geoplugin_longitude) || 0;
        
        if (latitude === 0 && longitude === 0) {
          reject(new Error('Invalid coordinates from geoplugin'));
          return;
        }
        
        resolve({
          latitude: latitude,
          longitude: longitude,
          city: data.geoplugin_city || 'Unknown',
          region: data.geoplugin_regionName || 'Unknown',
          country: data.geoplugin_countryName || 'Unknown',
          countryCode: data.geoplugin_countryCode || 'Unknown',
          postal: '',
          timezone: data.geoplugin_timezone || '',
          accuracy: 'IP-based (approximate)',
          source: 'geoplugin.net',
          timestamp: Date.now()
        });
      };
      
      // Handle errors
      script.onerror = () => {
        cleanup();
        reject(new Error('Failed to load geoplugin'));
      };
      
      // Add script to document
      document.head.appendChild(script);
    });
  }

  /**
   * Get fallback location if all services fail
   */
  getFallbackLocation() {
    // Default to a central location (e.g., New York)
    return {
      latitude: 40.7128,
      longitude: -74.0060,
      city: 'New York',
      region: 'New York',
      country: 'United States',
      countryCode: 'US',
      postal: '10001',
      timezone: 'America/New_York',
      accuracy: 'Fallback location',
      source: 'fallback',
      timestamp: Date.now(),
      isFallback: true
    };
  }

  /**
   * Get location with retry mechanism
   * @param {number} maxRetries - Maximum number of retries
   * @param {number} delayMs - Delay between retries in milliseconds
   */
  async getLocationWithRetry(maxRetries = 3, delayMs = 1000) {
    let lastError = null;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        console.log(`📍 Location attempt ${attempt}/${maxRetries}`);
        const result = await this.getLocationFromIP();
        if (result && result.latitude && result.longitude && result.latitude !== 0) {
          console.log('✅ Location obtained successfully');
          return result;
        }
      } catch (error) {
        lastError = error;
        console.warn(`Attempt ${attempt} failed:`, error.message);
        
        if (attempt < maxRetries) {
          // Wait before retrying with exponential backoff
          const waitTime = delayMs * Math.pow(2, attempt - 1);
          console.log(`Waiting ${waitTime}ms before retry...`);
          await new Promise(resolve => setTimeout(resolve, waitTime));
        }
      }
    }
    
    // Return fallback after all retries fail
    console.warn('⚠️ All retries failed, using fallback location');
    return this.getFallbackLocation();
  }

  /**
   * Get current stored position
   */
  getCurrentPosition() {
    return this.currentPosition;
  }

  /**
   * Clear cache
   */
  clearCache() {
    this.currentPosition = null;
    this.lastFetchTime = null;
    this.error = null;
  }

  /**
   * Check if location is available (always true for IP-based)
   */
  isLocationAvailable() {
    return true;
  }

  /**
   * Format location for display
   */
// src/services/ipLocationService.js

/**
 * Format location for display - Smart formatting without state for most countries
 */
formatLocation(location = this.currentPosition) {
  if (!location) return 'No location available';
  
  const city = location.city && location.city !== 'Unknown' ? location.city : '';
  const region = location.region && location.region !== 'Unknown' ? location.region : '';
  const country = location.country && location.country !== 'Unknown' ? location.country : '';
  const countryCode = location.countryCode || '';
  
  // Build parts array
  const parts = [];
  
  // Always add city if available
  if (city) parts.push(city);
  
  // Only add region/state for specific countries where it's commonly used
  // US, Canada, Australia, Brazil, Mexico, India, Germany, etc.
  const countriesWithRegion = ['US', 'USA', 'CA', 'AU', 'BR', 'MX', 'IN', 'DE', 'IT', 'ES', 'FR'];
  
  // Check if region should be included
  const shouldIncludeRegion = region && countriesWithRegion.includes(countryCode);
  
  // For US, skip the state (just show city and country)
  if (countryCode === 'US' || countryCode === 'USA') {
    // Skip region for US
    if (country) parts.push(country);
  } else {
    // For other countries, include region if it makes sense
    if (shouldIncludeRegion) {
      parts.push(region);
    }
    if (country) parts.push(country);
  }
  
  // If no city, use region or country as fallback
  if (parts.length === 0) {
    if (region) parts.push(region);
    if (country) parts.push(country);
  }
  
  // If still empty, use coordinates
  if (parts.length === 0) {
    return `${location.latitude?.toFixed(4) || 0}, ${location.longitude?.toFixed(4) || 0}`;
  }
  
  return parts.join(', ');
}

  /**
   * Get distance between two coordinates
   */
  getDistance(coord1, coord2) {
    if (!coord1 || !coord2) return 0;
    
    const R = 6371; // Earth's radius in kilometers
    const dLat = this.toRad((coord2.latitude || 0) - (coord1.latitude || 0));
    const dLon = this.toRad((coord2.longitude || 0) - (coord1.longitude || 0));
    const lat1 = this.toRad(coord1.latitude || 0);
    const lat2 = this.toRad(coord2.latitude || 0);
    
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
              Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  /**
   * Convert degrees to radians
   */
  toRad(degrees) {
    return degrees * (Math.PI / 180);
  }
}

// Create singleton instance
const ipLocationService = new IPLocationService();
export default ipLocationService;